import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Search, Upload, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/KpiCard";
import { api, errMsg } from "@/lib/api";
import { fmtNum, fmtEur } from "@/lib/format";

const monthLabel = (m) => {
  const d = new Date(`${m}-01T00:00:00`);
  const s = d.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const DemoCard = () => {
  const [count, setCount] = useState(0);
  const load = () => api.get("/demo-data").then((r) => setCount(r.data.count));
  useEffect(() => { load(); }, []);
  if (!count) return null;
  const remove = async () => {
    if (!window.confirm(`Eliminare definitivamente i ${count} record di prova? I dati reali e importati non vengono toccati.`)) return;
    try { const { data } = await api.delete("/demo-data"); toast.success(`${data.deleted} record di prova eliminati`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-6 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="demo-data-card">
      <p className="text-sm text-amber-800">Ci sono <b data-testid="demo-data-count">{count}</b> record di prova. Eliminali prima di importare i dati reali.</p>
      <Button variant="outline" onClick={remove} data-testid="delete-demo-data-button" className="border-amber-300"><Trash2 className="mr-2 h-4 w-4" /> Elimina dati di prova</Button>
    </div>
  );
};

const PreviewTable = ({ data }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="import-preview-table">
      <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr><th className="px-4 py-3">Mese</th><th className="px-4 py-3 text-right">Giorni</th><th className="px-4 py-3 text-right">Gasolio IVA incl.</th>
          <th className="px-4 py-3 text-right">KM aziendali</th><th className="px-4 py-3 text-right">Km Amazon</th><th className="px-4 py-3 text-right">Rimborso</th><th className="px-4 py-3 text-right">Già presenti</th></tr>
      </thead>
      <tbody className="divide-y">
        {data.months.map((m) => (
          <tr key={m.month} data-testid={`import-month-${m.month}`}>
            <td className="px-4 py-3 font-medium">{monthLabel(m.month)}</td>
            <td className="font-mono-num px-4 py-3 text-right">{m.days}</td>
            <td className="font-mono-num px-4 py-3 text-right">{fmtEur(m.total_amount)}</td>
            <td className="font-mono-num px-4 py-3 text-right">{m.kilometers ? fmtNum(m.kilometers, 0) : "—"}</td>
            <td className="font-mono-num px-4 py-3 text-right">{fmtNum(m.reimbursed_km, 0)}</td>
            <td className="font-mono-num px-4 py-3 text-right">{fmtEur(m.reimbursement)}</td>
            <td className={`font-mono-num px-4 py-3 text-right ${m.existing ? "font-semibold text-amber-600" : ""}`}>{m.existing}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminImport() {
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [overwrite, setOverwrite] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/stations").then((r) => setStations(r.data)); }, []);
  useEffect(() => setPreview(null), [stationId, file]);

  const send = async (confirm) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("station_id", stationId);
    fd.append("confirm", confirm);
    fd.append("overwrite", overwrite);
    setBusy(true);
    try {
      const { data } = await api.post("/import", fd);
      if (confirm) {
        toast.success(`Importazione completata: ${data.inserted} nuovi, ${data.updated} aggiornati, ${data.skipped} saltati`);
        setFile(null);
      } else setPreview(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const station = stations.find((s) => s.id === stationId);

  return (
    <div className="fade-up">
      <PageHeader title="Importa Excel" subtitle="Caricamento massivo dei mesi precedenti, una stazione alla volta." />
      <DemoCard />
      <div className="mb-6 grid gap-4 rounded-xl border border-slate-200 bg-white p-6 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label>Stazione</Label>
          <Select value={stationId} onValueChange={setStationId}>
            <SelectTrigger data-testid="import-station-select"><SelectValue placeholder="Seleziona stazione" /></SelectTrigger>
            <SelectContent>{stations.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>File Excel (.xlsx)</Label>
          <label className="flex h-9 cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 text-sm text-slate-600 transition-colors hover:border-slate-400">
            <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
            <span className="truncate" data-testid="import-file-name">{file ? file.name : "Scegli file..."}</span>
            <input type="file" accept=".xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
          </label>
        </div>
        <div className="text-xs text-slate-500 md:col-span-3">
          Colonne lette per ogni foglio mensile: <b>DATA</b>, <b>IMPORTO GASOLIO</b> (IVA incl.), <b>KM</b> aziendali (facoltativo), <b>KM AMAZON</b>, <b>RIMBORSO AMAZON</b> (tariffa €/km). Le righe senza importo gasolio e le date future vengono ignorate. I mezzi usciti restano vuoti e i record vengono importati come Approvati.
        </div>
        <div className="md:col-span-3">
          <Button onClick={() => send(false)} disabled={!stationId || !file || busy} data-testid="import-preview-button"><Search className="mr-2 h-4 w-4" /> Analizza file</Button>
        </div>
      </div>

      {preview && (
        <div className="rounded-xl border border-slate-200 bg-white" data-testid="import-preview">
          <div className="flex flex-col gap-1 border-b p-5">
            <p className="font-semibold">Anteprima per {station?.name}: <span data-testid="import-preview-rows">{preview.rows}</span> giorni trovati</p>
            <p className="text-xs text-slate-500">Fogli letti: {preview.sheets.join(", ")} · {preview.future_skipped} righe con data futura ignorate · {preview.existing} giorni già presenti</p>
          </div>
          <PreviewTable data={preview} />
          <div className="flex flex-col gap-4 border-t p-5 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} data-testid="import-overwrite-checkbox" />
              Sovrascrivi i giorni già presenti (es. dati di prova)
            </label>
            <Button onClick={() => send(true)} disabled={busy} data-testid="import-confirm-button"><Upload className="mr-2 h-4 w-4" /> Importa {preview.rows} giorni</Button>
          </div>
        </div>
      )}
    </div>
  );
}
